import React from "react";
import type { ControlProps } from "react-select";

import { faSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Button } from "~/ui";
import MultiSelector, { components, type MenuProps, type MultiSelectorOption } from "~/ui/selectors/MultiSelector";

import type { SalesforceObjectRecordType } from "./ObjectCard";

import styles from "./css/AiSuggestionsSelector.module.css";

const CustomTriggerButton = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
	(props, ref) => {
		return (
			<Button {...props} ref={ref} variant="primary-ai" className={styles.triggerButton}>
				<FontAwesomeIcon icon={faSparkles} className={styles.triggerButtonIcon} />
				AI Suggestions...
			</Button>
		);
	},
);

CustomTriggerButton.displayName = "CustomTriggerComponent";

const Control = ({ children, ...rest }: ControlProps<OptionType, true>) => {
	return (
		<components.Control {...rest} className={styles.control}>
			<div className={styles.selectorHeader}>
				<div className={styles.selectorHeaderTitle}>AI Suggestions</div>
				<div>Select the Salesforce records to fill in</div>
			</div>
			{children}
		</components.Control>
	);
};

interface GroupType {
	/** Name of the object */
	label: string;
	options: OptionType[];
}

export interface OptionType extends MultiSelectorOption {
	/** Name or ID of the Salesforce record */
	label: string;
	/** ID of the Salesforce record, should be unique */
	value: string;
	objectLabel: string;
	objectName: string;
	fields: SalesforceObjectRecordType["fields"];
}

interface AiSuggestionsProps {
	activeContact: SalesforceObjectRecordType;
	onAutoFillAiSuggestions: (selectedOptions: readonly OptionType[]) => void;
	isDisabled: boolean;
}

export default function AiSuggestionsSelector({
	activeContact,
	onAutoFillAiSuggestions,
	isDisabled,
}: AiSuggestionsProps) {
	const [selectorValue, setSelectorValue] = React.useState<readonly OptionType[]>([]);

	const optionsGroups: readonly GroupType[] = React.useMemo(() => {
		let groups: GroupType[] = [];

		if (activeContact?.fields) {
			const fieldsMarkedForAiSuggestion = activeContact.fields.filter(
				field => field.interactionType === "AI_SUGGESTION",
			);

			if (fieldsMarkedForAiSuggestion.length > 0) {
				groups.push({
					label: activeContact.label,
					options: [
						{
							label: activeContact.value || activeContact.id,
							value: activeContact.id,
							objectLabel: activeContact.label,
							objectName: activeContact.name,
							fields: fieldsMarkedForAiSuggestion,
						},
					],
				});
			}
		}

		const referenceGroupsDict: {
			[key: string]: GroupType;
		} = {};

		activeContact?.references?.forEach(reference => {
			const fieldsMarkedForAiSuggestion = reference.fields.filter(field => field.interactionType === "AI_SUGGESTION");

			if (fieldsMarkedForAiSuggestion.length > 0) {
				if (!referenceGroupsDict[reference.label]) {
					referenceGroupsDict[reference.label] = {
						label: reference.label,
						options: [],
					};
				}
				referenceGroupsDict[reference.label].options.push({
					label: reference.value || reference.id,
					value: reference.id,
					objectLabel: reference.label,
					objectName: reference.name,
					fields: fieldsMarkedForAiSuggestion,
				});
			}
		});

		return groups.concat(Object.values(referenceGroupsDict));
	}, [activeContact]);

	const selectedOptions = React.useCallback(
		(selectedOptions: readonly OptionType[]) => {
			setSelectorValue(selectedOptions);
		},
		[setSelectorValue],
	);

	const onAutoFillClick = React.useCallback(
		value => {
			onAutoFillAiSuggestions(value);
			setSelectorValue([]);
		},
		[onAutoFillAiSuggestions],
	);

	const Menu = React.useCallback(
		({ children, ...rest }: MenuProps<OptionType, true>) => {
			const { value } = rest.selectProps;
			// `value` should always be an array since we're using a MultiSelector,
			// but we have to check it to avoid TypeScript errors
			if (value && !("length" in value)) {
				return null;
			}

			// We use this wrapper function with `value` instead of directly using
			// `onAutoFillClick` with `selectorValue` to avoid a re-render when
			// the selection changes causing the menu to scroll to the top
			const handleAutoFillClick = () => {
				onAutoFillClick(value);
			};

			return (
				<components.Menu {...rest}>
					{children}
					<div className={styles.menuFooter}>
						{/* eslint-disable-next-line fellow/no-inline-disable*/}
						{/* eslint-disable-next-line react/jsx-no-bind */}
						<Button onClick={handleAutoFillClick} variant="primary" size="slim" disabled={value?.length === 0}>
							Auto fill and review
						</Button>
					</div>
				</components.Menu>
			);
		},
		[onAutoFillClick],
	);

	return (
		<MultiSelector
			options={optionsGroups}
			value={selectorValue}
			onChange={selectedOptions}
			isSearchable={true}
			CustomTriggerButton={CustomTriggerButton}
			components={{ Menu, Control }}
			isDisabled={optionsGroups.length === 0 || isDisabled}
			menuAlign="start"
			contentFixedWidth="280px"
			isOpen={isDisabled ? false : undefined}
		/>
	);
}
